import React from 'react';
import Button from '@mui/material/Button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddchartOutlinedIcon from '@mui/icons-material/AddchartOutlined';
import { Link, useParams } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import styles from './Goal.module.scss';
import useApiData from '../../../../../../hooks/useApiData';
import Loading from '../../../../Common/Loading/Loading';
import { getErrorComponentFromHttpError } from '../../../../Common/Error/Error';
import ProgressBar from '../../../../Common/ProgressBar/ProgressBar';
import DetailedGoalProgressChart from './DetailedGoalProgressChart/DetailedGoalProgressChart';
import {
  getAbsoluteTimestamp,
  getRelativeTimestamp,
} from '../../../../../../util/formatDate';
import { TeamLogMeasurementDialog } from '../../../../Common/LogMeasurementDialog/LogMeasurementDialog';
import Table from '../../../../Common/Table/Table/Table';
import { TeamEditMeasurementDialog } from '../../../../Common/EditMeasurementDialog/EditMeasurementDialog';
import useAuth from '../../../../../../hooks/useAuth';

function getDurationProgress(start, end, now) {
  if (now > end) {
    return 1;
  }
  if (now < start) {
    return 0;
  }
  return (now - start) / (end - start);
}

export default function Goal() {
  const [isLogMeasurementDialogOpen, setIsLogMeasurementDialogOpen] =
    React.useState(false);
  const [isEditLogMeasurementDialogOpen, setIsEditLogMeasurementDialogOpen] =
    React.useState(false);
  const [selectedGoalMeasurement, setSelectedGoalMeasurement] =
    React.useState(null);
  const { teamId, goalId } = useParams();
  const { currentUser } = useAuth();
  const team = currentUser.teams.filter((t) => t.id === teamId)?.[0] || {
    label: 'Unknown Team',
  };
  const isLead = team.isLead;
  const { data, error, isLoading, reload } = useApiData({
    path: `/user/teams/${teamId}/goals/${goalId}`,
    params: {
      includeTeams: true,
      includeGoalMeasures: true,
    },
  });

  function displayEditGoalMeasureDialog(goalMeasurement) {
    setSelectedGoalMeasurement(goalMeasurement);
    setIsEditLogMeasurementDialogOpen(true);
  }

  if (isLoading) {
    return <Loading message="Fetching goal..." />;
  }

  if (error || data === null) {
    return getErrorComponentFromHttpError(error);
  }

  const {
    label,
    isTeamGoal,
    description,
    startValue,
    endValue,
    startTime,
    endTime,
    teams,
    goalMeasures,
  } = data;

  const currentValue =
    goalMeasures.length > 0
      ? goalMeasures[goalMeasures.length - 1].value
      : startValue;

  const startTimeMs = new Date(startTime).getTime();
  const endTimeMs = new Date(endTime).getTime();
  const nowMs = new Date().getTime();
  const timeProgress = getDurationProgress(startTimeMs, endTimeMs, nowMs);
  const goalProgress = (currentValue - startValue) / (endValue - startValue);
  const goalProgressDesiredValue = Math.floor(
    timeProgress * (endValue - startValue) + startValue,
  );
  const goalProgressDesired =
    (goalProgressDesiredValue - startValue) / (endValue - startValue);

  return (
    <div className={styles.goal}>
      {isLead && (
        <>
          <TeamLogMeasurementDialog
            goal={data}
            isOpen={isLogMeasurementDialogOpen}
            close={() => setIsLogMeasurementDialogOpen(false)}
            reload={reload}
            hideEditButton
          />
          {selectedGoalMeasurement && (
            <TeamEditMeasurementDialog
              goalLabel={data.label}
              goalMeasurement={selectedGoalMeasurement}
              isOpen={isEditLogMeasurementDialogOpen}
              close={() => {
                setIsLogMeasurementDialogOpen(false);
                setSelectedGoalMeasurement(null);
              }}
              reload={reload}
            />
          )}
        </>
      )}
      <div className={styles.header}>
        <h1>{label}</h1>
        <div className={styles.row}>
          {isTeamGoal && (
            <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              component={Link}
              to={`/user/teams/${teamId}/goals/hierarchy/edit`}
            >
              Edit
            </Button>
          )}
        </div>
      </div>
      <div className={styles.mainRow}>
        <div className={styles.stack}>
          {(description && (
            <p className={styles.description}>{description}</p>
          )) || <p className={styles.noData}>No description for this goal</p>}
        </div>
        <div className={styles.stack}>
          <Tooltip
            title={`Current: ${currentValue}, optimal: ${goalProgressDesiredValue}`}
            className={styles.stack}
          >
            <div className={styles.charts}>
              <p className={styles.label}>Goal Progress:</p>
              <ProgressBar
                value={goalProgress}
                color="linear-gradient(90deg, rgb(158 212 131), rgb(146 234 178))"
                trailingDot
                trailingDotColor="rgb(105 184 105)"
              />

              <ProgressBar
                value={goalProgressDesired}
                color="linear-gradient(90deg, rgb(127 151 230), rgb(176 221 254))"
                trailingDot
                trailingDotColor="rgb(103 155 219)"
              />
              <div className={styles.row}>
                <p className={styles.value}>{startValue}</p>
                <p className={styles.value}>{endValue}</p>
              </div>
            </div>
          </Tooltip>
          <p className={styles.label}>Time:</p>
          <ProgressBar
            value={timeProgress}
            color="linear-gradient(90deg, rgb(212 141 186), rgb(214 183 255)"
            trailingDot
            trailingDotColor="rgb(170 124 232)"
          />
          <div className={styles.row}>
            <p className={styles.value}>
              {getAbsoluteTimestamp(new Date(startTime))}
            </p>
            <p className={styles.value}>
              {getAbsoluteTimestamp(new Date(endTime))}
            </p>
          </div>
        </div>
        <div className={styles.stack}>
          <p className={styles.label}>Team{teams.length > 0 && 's'}:</p>
          <div className={styles.row}>
            <p className={styles.teams}>
              {teams.map((t, i, a) => (
                <React.Fragment key={`goal-${goalId}${t.id}`}>
                  <span className={styles.teamName}>{t.label}</span>
                  {i !== a.length - 1 && ', '}
                </React.Fragment>
              ))}
            </p>
          </div>
          <p className={styles.label}>Latest measurement:</p>
          {(goalMeasures.length === 0 && (
            <p className={styles.noData}>Not recorded</p>
          )) || (
            <p className={styles.value}>
              {goalMeasures[goalMeasures.length - 1].value}{' '}
              <span className={styles.valueSecondary}>
                {getAbsoluteTimestamp(
                  new Date(goalMeasures[goalMeasures.length - 1].time),
                )}
              </span>
              <span className={styles.valueSecondary}>
                (
                {getRelativeTimestamp(
                  new Date(goalMeasures[goalMeasures.length - 1].time),
                )}
                )
              </span>
            </p>
          )}
          {isLead && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setIsLogMeasurementDialogOpen(true)}
              startIcon={<AddchartOutlinedIcon />}
            >
              Log Measurement
            </Button>
          )}
        </div>
      </div>

      <div className={styles.chart}>
        <DetailedGoalProgressChart
          label={label}
          startValue={startValue}
          endValue={endValue}
          startTime={startTime}
          endTime={endTime}
          goalMeasures={goalMeasures}
        />
      </div>

      <div>
        <Table
          rows={goalMeasures}
          topButtons={
            isLead
              ? [
                  {
                    label: 'Log Measurement',
                    variant: 'outlined',
                    startIcon: <AddchartOutlinedIcon />,
                    handler: () => setIsLogMeasurementDialogOpen(true),
                  },
                ]
              : []
          }
          keyColumn="id"
          allowSort
          defaultSortKey="time"
          defaultSortOrder="desc"
          title={`Goal Measurements`}
          rowFunction={displayEditGoalMeasureDialog}
          rowFunctionIcon={<EditOutlinedIcon />}
          rowFunctionLabel="Edit measurement"
          allowPagination
          pageSize={10}
          columns={[
            {
              id: 'time',
              label: 'Date',
              date: true,
            },
            {
              id: 'value',
              label: 'Value',
              numeric: true,
            },
          ]}
        />
      </div>
    </div>
  );
}
